// ProgressBar.tsx
import React from 'react'
import styled from 'styled-components'
import { images } from '../images'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { IoChevronBack } from 'react-icons/io5'

const ProgressBarWrapper = styled.div`
  position: sticky;
  top: 0;
  padding-top: ${({ theme }) => theme.responsiveSpacing.small};
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  z-index: 1;
  background-color: white;
`

const ProgressBarContainer = styled.div<{ withDotPadding?: boolean }>`
  position: relative;
  background-color: ${({ theme }) => theme.colors.lighterGray};
  border-radius: ${({ theme }) => theme.spacing.xsmall};
  height: 6px;
  padding: ${({ withDotPadding }) => withDotPadding ? '0 10px' : '0'};
  margin: ${({ withDotPadding }) => withDotPadding ? '0 15px' : '0'};
`

const CurrentStep = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`
const StepOutOfSeparator = styled.span`
  margin: 0 3px;
`

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress * 100}%;
  border-radius: ${({ theme }) => theme.spacing.xsmall};
  background-color: ${({ theme }) => theme.colors.primary};
  transition: width 0.5s ease-in-out; // Add this line
`

const InfoContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const LogoOuterWrapper = styled.div`
  display: flex;
`

const LogoWrapper = styled.div`
  line-height: 0; // Ensures no extra line-height space

  source {
    height: ${({ theme }) => theme.responsiveSpacing.medium};
    min-height: ${({ theme }) => theme.spacing.small}; // Set min height to ensure that the logo does not get extremely small
  }

  img {
     height: ${({ theme }) => theme.responsiveSpacing.medium};
     min-height: ${({ theme }) => theme.spacing.small}; // Set min height to ensure that the logo does not get extremely small
  }
`

const BackArrow = styled.button < { visible?: boolean } >`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: ${({ theme }) => theme.fontSizes.large};
  color: ${({ theme }) => theme.colors.lightText};

  &:active {
    transform: translateY(-50%) scale(0.85);
    color: ${({ theme }) => theme.colors.primary};
  }
`

const StepInfo = styled.div<{ visible?: boolean }>`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 700;
`

const ProgressDotContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
`

const ProgressDot = styled.div<{ active: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.lighterGray};
  transition: background-color 0.5s ease-in-out;
`

interface ProgressBarProps {
  currentStep: number
  totalSteps: number
  barrierSteps: number[]
  onBack: () => void
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  barrierSteps,
  onBack
}) => {
  const segmentDots = useFeatureValue('progressbar-segment-dots', false)
  const stepCounter = useFeatureValue('progressbar-step-counter', true)
  const backButton = useFeatureValue('progressbar-back-button', true)
  barrierSteps = barrierSteps.concat([totalSteps]) // simplifies calculation

  let progress = 0

  if (!segmentDots) {
    progress = currentStep / totalSteps
  } else {
    const segmentLength = 1 / barrierSteps.length
    for (let i = 0; i < barrierSteps.length; i++) {
      if (currentStep <= barrierSteps[i]) {
        const stepsInSegment = barrierSteps[i] - (i === 0 ? 0 : barrierSteps[i - 1])
        const segmentProgress = stepsInSegment - (barrierSteps[i] - currentStep)
        progress = i * segmentLength + segmentProgress / stepsInSegment * segmentLength
        break
      }
    }
  }

  return (
    <ProgressBarWrapper>
      <InfoContainer>
        {backButton && <BackArrow visible={currentStep !== 0} onClick={onBack}><IoChevronBack /></BackArrow>}
        <LogoOuterWrapper>
          <LogoWrapper>
            <picture>
              <source srcSet={images.LogoWebp} type="image/webp" />
              <img src={images.Logo} alt="logo" />
            </picture>
          </LogoWrapper>
        </LogoOuterWrapper>
        <StepInfo visible={stepCounter}>
          <CurrentStep>{currentStep}</CurrentStep><StepOutOfSeparator>/</StepOutOfSeparator>{totalSteps}
        </StepInfo>
      </InfoContainer>
      <ProgressBarContainer withDotPadding={segmentDots}>
        <ProgressFill progress={progress} />
        {segmentDots && <ProgressDotContainer>
          <ProgressDot active={true} />
          {barrierSteps.map((step, index) => (
            <ProgressDot key={index} active={currentStep >= step} />
          ))}
        </ProgressDotContainer>
        }
      </ProgressBarContainer>
    </ProgressBarWrapper>
  )
}

export default ProgressBar
